import { connect, useDispatch } from 'react-redux';
import { Form, FormRenderProps } from 'react-final-form';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dispatch } from 'redux';
import arrayMutators from 'final-form-arrays';

import {
  getPublishRulesActiveTreeItem,
  getPublishRulesFormData,
  getPublishRulesFormRef,
  getPublishRulesHistoryChanges,
  getPublishRulesTree,
} from 'PublishRulesPackage/MainModule/selectors/publishRulesSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { IKnmHistoryItemChanges } from 'KnmPackage/KnmHistoriesModule/interfaces/IKnmHistoryItemChanges';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { getCatalogKnmStatuses248SelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmStatusSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmStatusSelectOption';
import { IPublishRules } from 'APIPackage/PublishRulesModule/interfaces/IPublishRules';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';

import { IPublishRulesKnmScheduledForm } from '../interfaces/IPublishRulesKnmScheduledForm';
import { PublishRulesFormRef } from '../reducers/publishRulesReducer';
import {
  createOrUpdatePublishRules,
  deletePublishRules,
  loadPublishRulesData,
  setPublishRulesFormRef,
} from '../actions/publishRulesActions';
import { PUBLISH_RULES_FORM_ID } from '../constants/publishRulesFieldsElementsIds';
import { PublishRulesFormAdditionalSettings } from './PublishRulesFormAdditionalSettings';
import { IPublishRulesTreeItem } from '../interfaces/IPublishRulesTreeItem';
import { PublishRulesHistory } from './PublishRulesHistory';
import { PublishRulesFormLeavingGuard } from './PublishRulesFormLeavingGuard';
import { IPublishRulesKnmScheduledFormRef } from '../interfaces/IPublishRulesKnmScheduledFormRef';
import { makeBlockTitle } from '../utils/makeBlockTitle';
import { getPublishRulesKnmScheduledFormInitialValues } from '../common/getPublishRulesKnmScheduledFormInitialValues';
import { getKnmReasonType } from '../utils/getKnmReasonType';
import { makePlannedValues } from '../common/makePlannedValues';
import { PublishRulesApproveTypeEnum } from '../enums/publishRulesApproveTypeEnum';

export type IPublishRulesKnmScheduledFormRenderProps = FormRenderProps<IPublishRulesKnmScheduledForm>;
interface IPublishRulesKnmScheduledFormProps {
  activeTreeItem: IPublishRulesTreeItem;
  tree: IPublishRulesTreeItem[];
  formRef?: IPublishRulesKnmScheduledFormRef;
  formData?: IPublishRules;
  historyChanges?: IKnmHistoryItemChanges[];
  knmStatusesSelectOptions?: IKnmStatusSelectOption[];
}

const PublishRulesKnmScheduledFormWithoutStore = ({
  activeTreeItem,
  tree,
  formRef,
  formData,
  historyChanges,
  knmStatusesSelectOptions,
}: IPublishRulesKnmScheduledFormProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [isConfirmDeleteSettingsVisible, setIsConfirmDeleteSettingsVisible] = useState<boolean>(false);
  const [reasonTypesSelectOptions, setReasonTypesSelectOptions] = useState<ISelectOption[]>([]);
  const [allowedKnmStatusesSelectOptions, setAllowedKnmStatusesSelectOptions] = useState<ISelectOption[]>([]);

  const setFormRef = useCallback(
    (newFormRef?: PublishRulesFormRef) => dispatch(setPublishRulesFormRef(newFormRef)),
    [],
  );

  // Значения по умолчанию
  const initialValues = useMemo(
    () => getPublishRulesKnmScheduledFormInitialValues(activeTreeItem, knmStatusesSelectOptions),
    [activeTreeItem.category, knmStatusesSelectOptions],
  );

  // Загрузка данных
  useEffect(() => {
    dispatch(loadPublishRulesData(activeTreeItem.category));
  }, [activeTreeItem.category]);

  // Инициализация полученными данными
  useEffect(() => {
    if (formData && formRef && knmStatusesSelectOptions) {
      const { entries, ervkKnmReasonTypes, allowedStatusCodes } = formData;

      const reasonTypesSelectOptionsFromData = ervkKnmReasonTypes?.map(item => getKnmReasonType(item));
      const allowedStatusesSelectOptions = knmStatusesSelectOptions.filter(item =>
        allowedStatusCodes?.includes(item.code),
      );

      reasonTypesSelectOptionsFromData && setReasonTypesSelectOptions(reasonTypesSelectOptionsFromData);
      allowedStatusesSelectOptions && setAllowedKnmStatusesSelectOptions(allowedStatusesSelectOptions);

      if (entries?.length !== undefined && entries?.length > 0) {
        const entry = entries?.find(item => item.approveType === PublishRulesApproveTypeEnum.REQUIRED) || entries?.[0];

        const data: IPublishRulesKnmScheduledForm = makePlannedValues(
          entry,
          reasonTypesSelectOptionsFromData || [],
          knmStatusesSelectOptions,
        );

        formRef.current.initialize(data);
      }
    }
  }, [formData, formRef, knmStatusesSelectOptions]);

  const blockTitle = useMemo(() => makeBlockTitle(tree, activeTreeItem), [tree, activeTreeItem]);

  const handleFormSubmit = useCallback(
    (data: IPublishRulesKnmScheduledForm) => {
      const newData: IPublishRules = {
        entries: [
          {
            approveType: null,
            publicationCategoryCode: activeTreeItem.category,
            settings: {
              mainRule: {
                passportStatusCodes: data.uponAttainingStatus?.map(item => item.value) || null,
                publicationMode: data.publicationDate || null,
              },
              additionalRules:
                data.additionalSettings?.map(setting => ({
                  reasonTypeId: (setting.typeOfBasis?.id as number) || null,
                  rule: {
                    passportStatusCodes: setting.uponAttainingStatus?.map(status => status.value) || null,
                    publicationMode: setting.publicationDate || null,
                  },
                })) || null,
            },
          },
        ],
      };

      dispatch(createOrUpdatePublishRules(activeTreeItem.category, newData));
    },
    [activeTreeItem.category],
  );

  const handleApplyDeleteSetting = useCallback(() => {
    dispatch(deletePublishRules(activeTreeItem.category, initialValues));
  }, [formRef, activeTreeItem.category, initialValues]);

  const handleOnClearSetting = useCallback(() => {
    setIsConfirmDeleteSettingsVisible(true);
  }, []);

  const renderForm = ({ handleSubmit, form }: FormRenderProps<IPublishRulesKnmScheduledForm>) => {
    const currentFormRef = useRef<IPublishRulesKnmScheduledFormRenderProps['form']>(form);

    useEffect(() => {
      if (currentFormRef) {
        setFormRef(currentFormRef);
      }
    }, [setFormRef, currentFormRef]);

    return (
      <form id={PUBLISH_RULES_FORM_ID} onSubmit={handleSubmit}>
        <KnmCollapse
          theme="fields"
          title={blockTitle}
          onClose={activeTreeItem.haveSavedSettings ? handleOnClearSetting : undefined}
          openByDefault
          isNew={!activeTreeItem.haveSavedSettings}
        >
          <>
            <FieldBlock label="Основное правило">
              <Row notWrap>
                <FieldBlock label="По достижении статуса" placementLabel="left" color="black" margin="right">
                  <SelectInputField
                    className="shared-row-field"
                    name="uponAttainingStatus"
                    isMulti
                    options={allowedKnmStatusesSelectOptions}
                    placeholder="Выберите статус"
                    validate={makeRequired(REQUIRED_ERROR)}
                  />
                </FieldBlock>
                <FieldBlock label="Дата публикации" placementLabel="left" color="black">
                  <DatePickerField
                    name="publicationDate"
                    placeholder="Выберите дату"
                    validate={makeRequired(REQUIRED_ERROR)}
                    dateFormat="dd.MM"
                    showCalendarYear={false}
                  />
                </FieldBlock>
              </Row>
            </FieldBlock>
            <PublishRulesFormAdditionalSettings
              statusesSelectOptions={allowedKnmStatusesSelectOptions}
              reasonTypesSelectOptions={reasonTypesSelectOptions}
            />
            {historyChanges && (
              <Row>
                <PublishRulesHistory historyChanges={historyChanges} />
              </Row>
            )}
          </>
        </KnmCollapse>
        <PublishRulesFormLeavingGuard formRef={formRef} />
      </form>
    );
  };

  return (
    <>
      <Form<IPublishRulesKnmScheduledForm>
        onSubmit={handleFormSubmit}
        render={renderForm}
        mutators={{ ...arrayMutators }}
        initialValues={initialValues}
      />
      <ConfirmModal
        visible={isConfirmDeleteSettingsVisible}
        title="Подтверждение"
        text="Удалить текущие настройки?"
        onApply={handleApplyDeleteSetting}
        onClose={() => setIsConfirmDeleteSettingsVisible(false)}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  activeTreeItem: getPublishRulesActiveTreeItem(state),
  tree: getPublishRulesTree(state),
  formRef: getPublishRulesFormRef(state),
  formData: getPublishRulesFormData(state),
  historyChanges: getPublishRulesHistoryChanges(state),
  knmStatusesSelectOptions: getCatalogKnmStatuses248SelectOptions(state),
});

export const PublishRulesKnmScheduledForm = connect(mapStateToProps)(PublishRulesKnmScheduledFormWithoutStore);
